import React, {useState, useEffect, useMemo} from "react";
import { useParams, Link } from 'react-router-dom'
import axios from "axios";

import TopNav from "../components/TopNav";
import "./TourPage.css";

const TOURS_CACHE_KEY = "tpAv_tours_cache_v1";

// Helper to format date
const formatDate = (epoch) => {
    if (!epoch || epoch === 0) return "Not yet flown";
    // Assuming epoch is seconds (PHP standard)
    return new Date(epoch * 1000).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    });
};

// Helper to calculate days since last leg
const getDaysSinceLastLeg = (legs) => {
    if (!legs || !Array.isArray(legs)) return null;
    const flown = legs.filter(l => l.epoch > 0);
    if (flown.length === 0) return null;
    
    const lastEpoch = Math.max(...flown.map(l => l.epoch));
    const now = Date.now() / 1000;
    const diff = now - lastEpoch;
    return Math.floor(diff / (3600 * 24));
};

// Memoized Image Component
const TourImage = React.memo(({ id, alt, darkMode }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    return (
        <div className={`tp-card ${darkMode ? 'tp-dark-mode' : ''}`}>
            <div className="tp-image-container">
                {loading && !error && (
                    <div className="tp-spinner-overlay">
                        <div className="tp-spinner"></div>
                    </div>
                )}
                <img
                    className={`tp-image ${loading ? 'tp-hidden' : ''}`}
                    src={`${process.env.PUBLIC_URL}/tours/tour_${id}.jpg`}
                    alt={alt || 'Tour Image'}
                    loading="lazy"
                    onLoad={() => setLoading(false)}
                    onError={() => { setLoading(false); setError(true); }}
                />
                {error && <div style={{padding: '20px', textAlign: 'center'}}>Image not available</div>}
            </div>
        </div>
    );
});

// Table Component with Pagination Only
const TourLegsTable = ({ legs, darkMode }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    // Pagination
    const totalPages = Math.ceil((legs || []).length / itemsPerPage);
    const currentItems = (legs || []).slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    return (
        <div className={`tp-card ${darkMode ? 'tp-dark-mode' : ''}`}>
            {/* <h2 className="tp-card-title">Tour Legs</h2> */}
            
            <div className="tp-table-container">
                <table className="tp-table">
                    <thead>
                        <tr>
                            <th>Leg</th>
                            <th>Origin</th>
                            <th>Destination</th>
                            <th>Date Completed</th>
                        </tr>
                    </thead>
                    <tbody>
                        {currentItems.length > 0 ? (
                            currentItems.map((leg, index) => (
                                <tr key={index} className={leg.epoch > 0 ? 'tp-row-flown' : 'tp-row-pending'}>
                                    <td>{leg.legnum}</td>
                                    <td>{leg.orig}</td>
                                    <td>{leg.dest}</td>
                                    <td>{formatDate(leg.epoch)}</td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" style={{textAlign: 'center'}}>No legs found</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {totalPages > 1 && (
                <div className="tp-pagination">
                    <button 
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        className="tp-page-btn"
                    >
                        Previous
                    </button>
                    <span style={{alignSelf: 'center'}}>
                        Page {currentPage} of {totalPages}
                    </span>
                    <button 
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        className="tp-page-btn"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

const TourPage = (props) => {
    const { id } = useParams()
    const [data, setData] = useState([]);
    const [legData, setLegData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
      getTour();
      return () => {}
    }, [id])


    const getTour = async (obj) => {
        // Get the tours data
        try {
            const raw = localStorage.getItem(TOURS_CACHE_KEY);
            if (raw) {
                const parsed = JSON.parse(raw);
                const cachedTours = parsed?.tours;
                if (Array.isArray(cachedTours)) {
                    setError(null);
                    for(let i=0; i<cachedTours.length; i++){
                        const row = cachedTours[i];
                        if(row.id === id){
                            console.log(row)
                            setData(row);
                            break;
                        }
                    }
                }
            }
        } catch (e) {
            // Ignore cache parse/access errors and fall back to network.
        }

        try {
            console.log("getTour()")
            setLoading(true);
            const response = await axios.get("/api/tours/get_selected", {
            params: { _t: Date.now(),  id: id }
            });                                
            console.log(response);            

            console.log(response.data)
            setLegData(response.data.tourlegs); 
        }
        catch (e) {
            console.log(e)
            setError(e);
            setData([]); 
        }
        finally{
            setLoading(false); // Stop loading after any error
        }
    }    

    // Derived State
    const tourStatus = useMemo(() => {
        if (!legData || legData.length === 0) return 'unknown';
        const allFlown = legData.every(l => l.epoch > 0);
        const noneFlown = legData.every(l => l.epoch === 0);
        
        if (allFlown) return 'completed';
        if (noneFlown) return 'not-started';
        return 'in-progress';
    }, [legData]);

    const daysSinceLast = useMemo(() => getDaysSinceLastLeg(legData), [legData]);

    return(
        <React.Fragment>
            <TopNav page={props.page} darkMode={props.darkmode} />
            <div>{error}</div>
            <div className={`tp-container ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                
                <Link to="/tours" className="tp-back-link">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                        <path fillRule="evenodd" d="M15 8a.5.5 0 0 0-.5-.5H2.707l3.147-3.146a.5.5 0 1 0-.708-.708l-4 4a.5.5 0 0 0 0 .708l4 4a.5.5 0 0 0 .708-.708L2.707 8.5H14.5A.5.5 0 0 0 15 8z"/>
                    </svg>
                    Back to Tours
                </Link>

                {/* Header Section */}
                <header className="tp-header">
                    <h1 className="tp-title">
                        {data?.name && data.name}
                    </h1>
                    
                    {tourStatus === 'completed' && (
                        <span className="tp-status-badge tp-status-completed">Completed</span>
                    )}
                    {tourStatus === 'in-progress' && (
                        <span className="tp-status-badge tp-status-inprogress">In Progress</span>
                    )}
                    {tourStatus === 'not-started' && (
                        <span className="tp-status-badge tp-status-notstarted">Not Started</span>
                    )}
                </header>

                {/* Loading Overlay for initial fetch */}
                {loading && !legData.length && (
                    <div className="tp-spinner-overlay" style={{position: 'fixed'}}>
                        <div className="tp-spinner"></div>
                    </div>
                )}

                <div className="tp-grid">
                    {/* Left Column */}
                    <div className="tp-left-col">
                        <TourImage id={id} alt={data?.name} darkMode={props.darkmode} />
                        
                        <TourLegsTable legs={legData} darkMode={props.darkmode} />
                    </div>

                    {/* Right Column */}
                    <div className="tp-right-col">
                        
                        {/* Status Messages */}
                        {tourStatus === 'completed' && (
                            <div className={`tp-message-box tp-message-success ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                                <h3>Congratulations!</h3>
                                <p>You have successfully completed this tour.</p>
                            </div>
                        )}

                        {tourStatus === 'in-progress' && daysSinceLast !== null && (
                            <div className={`tp-message-box tp-message-info ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                                <h3>Keep it up!</h3>
                                <p>It has been {daysSinceLast} days since your last leg. We'd love to see you back in the skies soon to complete this tour!</p>
                            </div>
                        )}

                        {tourStatus === 'not-started' && (
                            <div className={`tp-message-box tp-message-info ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                                <h3>Ready for takeoff?</h3>
                                <p>You haven't started this tour yet. Start your engines!</p>
                            </div>
                        )}

                        {/* Summary */}
                        <div className={`tp-card ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                            {/* <h2 className="tp-card-title">Summary</h2> */}
                            <div className="tp-summary">
                                {data?.summary || "No summary available."}
                            </div>
                        </div>

                        {/* Description */}
                        <div className={`tp-card ${props.darkmode ? 'tp-dark-mode' : ''}`}>
                            <h2 className="tp-card-title">Description</h2>
                            <div className="tp-description">
                                {data?.descrip ? (
                                    data.descrip
                                ) : "No description available."}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>
    )

}
export default TourPage;
